import { z } from 'zod';
import { idParamSchema, paginationQuerySchema } from './common';
import { importEntitySchema, importRowStatusSchema, importWriteModeSchema } from './enums';

/**
 * Excel import inputs.
 *
 * The upload itself is multipart and carries only the file and the entity —
 * everything structured travels in these JSON requests, where the ordinary
 * validation pipe applies unchanged.
 */

/** Column indices are 0-based **within a block**, not within the sheet. */
const columnIndexSchema = z.coerce.number().int().min(0).max(199);

/**
 * How a text date is read.
 *
 * The single most dangerous setting in the importer. `03/04/2024` is 3 April
 * under `dmy` and 4 March under `mdy`, and nothing in the file says which the
 * person who typed it meant. Defaults to `dmy`, which is what this lab writes.
 */
export const dateOrderSchema = z.enum(['dmy', 'mdy', 'ymd']);
export type DateOrder = z.infer<typeof dateOrderSchema>;

export const DATE_ORDER_LABELS: Record<DateOrder, string> = {
  dmy: 'Day / month / year — 03/04/2024 is 3 April',
  mdy: 'Month / day / year — 03/04/2024 is 4 March',
  ymd: 'Year / month / day — 2024/03/04 is 4 March',
};

/**
 * Run options.
 *
 * Stored as JSON on the run and re-parsed on the way out, so every field has a
 * default for the same reason every print-template field does: a run written
 * before a field existed must still load.
 */
export const importOptionsSchema = z.object({
  date_order: dateOrderSchema.default('dmy'),
  write_mode: importWriteModeSchema.default('create_and_update'),
  /**
   * What an empty cell means on a row that already exists.
   *
   * True — the default — means "leave what is there alone". False means "write
   * the blank through". This is what stops a partial rate list, uploaded to
   * change nine prices, wiping the turnaround time off every other test.
   */
  preserve_existing: z.boolean().default(true),
  /** Create a Department from a group heading that does not match one. */
  create_departments: z.boolean().default(true),
  /**
   * Create a LabTest from an unrecognised name in a "tests charged" list.
   *
   * Off by default. A typo in a history sheet would otherwise add a priceless
   * test to the live catalogue, where the front desk can book it.
   */
  create_missing_tests: z.boolean().default(false),
});
export type ImportOptions = z.infer<typeof importOptionsSchema>;

/** The options a fresh run starts with, without needing a parse at the call site. */
export const DEFAULT_IMPORT_OPTIONS: ImportOptions = importOptionsSchema.parse({});

/**
 * Everything the operator can change without re-uploading.
 *
 * Changing any of it re-reads the staged cells — which are never rewritten —
 * so the preview is always the same bytes read a different way.
 */
export const updateImportMappingSchema = z.object({
  sheet_name: z.string().trim().min(1).optional(),
  /** 1-based, as Excel numbers rows in its gutter. */
  header_row: z.coerce.number().int().min(1).max(200).optional(),
  /**
   * How many side-by-side tables share the header row. Their rate list has two.
   *
   * The primary control. The server turns it into column offsets: it keeps the
   * detected geometry when the counts agree — which is what handles the blank
   * spacer column between their two blocks — and divides the header evenly
   * otherwise. The mapping screen shows the resulting headers per block, so a
   * wrong division is visible rather than discovered after a commit.
   */
  block_count: z.coerce.number().int().min(1).max(4).optional(),
  /**
   * The offsets outright, for the sheet the division gets wrong.
   *
   * An escape hatch, not the normal path. Wins over `block_count` when both are
   * sent.
   */
  block_offsets: z.array(columnIndexSchema).min(1).max(4).optional(),
  /** Column whose lone value is a heading carried down. Null clears it. */
  group_column: columnIndexSchema.nullish(),
  /** `{ name: 1, price: 2 }` — target field to source column. */
  mapping: z.record(z.string(), columnIndexSchema).optional(),
  options: importOptionsSchema.partial().optional(),
});
export type UpdateImportMappingInput = z.infer<typeof updateImportMappingSchema>;

export const importRowQuerySchema = paginationQuerySchema.extend({
  status: importRowStatusSchema.optional(),
  block_index: z.coerce.number().int().min(0).max(3).optional(),
});
export type ImportRowQuery = z.infer<typeof importRowQuerySchema>;

export const importRunQuerySchema = paginationQuerySchema.extend({
  entity: importEntitySchema.optional(),
});
export type ImportRunQuery = z.infer<typeof importRunQuerySchema>;

/**
 * Teaching the importer that a name means a test.
 *
 * Persisted beyond the run: an alias accepted once resolves automatically in
 * every later import, which is the only thing that stops a re-import repeating
 * the same forty decisions.
 */
export const createTestAliasSchema = z.object({
  source_text: z.string().trim().min(1, 'Enter the name as it appears in the sheet'),
  test_id: idParamSchema,
});
export type CreateTestAliasInput = z.infer<typeof createTestAliasSchema>;

export const testAliasQuerySchema = paginationQuerySchema.extend({
  test_id: idParamSchema.optional(),
});
export type TestAliasQuery = z.infer<typeof testAliasQuerySchema>;
